import React from "react";
import { motion } from "framer-motion";
import { MapPin, Timer, DollarSign, ShieldCheck, Star } from "lucide-react";

const PROPS = [
  {
    num: "01",
    icon: MapPin,
    title: "Locally Owned",
    desc: "Born and based in Syracuse. Your neighbors — not a franchise out of state.",
  },
  {
    num: "02",
    icon: Timer,
    title: "Weekend Service",
    desc: "Weekdays are fully booked — we're taking Saturday & Sunday jobs right now. Pick a 2-hour slot.",
  },
  {
    num: "03",
    icon: DollarSign,
    title: "Upfront Pricing",
    desc: "Flat quotes before we lift a thing. No surprise fees, ever.",
  },
  {
    num: "04",
    icon: ShieldCheck,
    title: "Fully Insured",
    desc: "Licensed and insured crew — your home, yard, and floors are protected.",
  },
];

export const WhyChooseUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="bg-offwhite py-24 sm:py-32 border-b-2 border-navy"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="label-pill">
              <span className="h-1.5 w-1.5 bg-gold" />
              03 — Why choose us
            </span>
            <h2 className="font-display uppercase text-navy text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
              Hard-working <br /> crew. <span className="text-gold">Honest</span> price.
            </h2>
            <p className="mt-6 font-body text-navy/75 text-base sm:text-lg max-w-md">
              We built B&amp;C on showing up on time, working harder than you
              expect, and leaving the place cleaner than we found it.
            </p>

            {/* Minority Owned Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              data-testid="minority-owned-badge"
              className="mt-10 inline-flex flex-col border-2 border-navy bg-navy text-white"
            >
              <div className="flex items-center gap-3 px-6 py-3 bg-gold text-navy border-b-2 border-navy">
                <Star className="h-5 w-5 fill-navy" strokeWidth={2.5} />
                <span className="font-display uppercase tracking-[0.18em] text-sm">
                  Proudly
                </span>
              </div>
              <div className="px-6 py-5">
                <p className="font-display uppercase text-3xl sm:text-4xl leading-none">
                  RN-Owned
                  <br />
                  Minority-Owned
                </p>
                <p className="mt-2 text-white/70 text-xs uppercase tracking-[0.2em]">
                  Syracuse · New York
                </p>
              </div>
            </motion.div>
          </div>

          <div
            data-testid="why-us-grid"
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-0 border-2 border-navy bg-white"
          >
            {PROPS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className={`relative p-8 sm:p-10 ${
                    i % 2 === 0 ? "sm:border-r-2 border-navy" : ""
                  } ${i < 2 ? "border-b-2 border-navy" : ""}`}
                  data-testid={`why-us-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="absolute top-4 right-5 font-display text-5xl text-navy/10">
                    {p.num}
                  </span>
                  <div className="h-14 w-14 flex items-center justify-center bg-navy text-gold border-2 border-navy mb-6">
                    <Icon className="h-7 w-7" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display uppercase text-2xl text-navy leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 font-body text-navy/75 leading-relaxed">
                    {p.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
